import React from "react";

const PACK_DIM_ORDER = [
  { key: "brand_alignment", label: "Brand Alignment vs Positioning" },
  { key: "ecommerce_shelf", label: "E-commerce Shelf Performance" },
  { key: "aisle_resiliency", label: "Retail Aisle Resiliency" },
  { key: "traditional_retail_india", label: "Traditional Indian Retail Presence" },
  { key: "distinctiveness", label: "Distinctiveness vs Competition" },
  { key: "hierarchy_eye_tracking", label: "Information Hierarchy & Eye-tracking" },
  { key: "audience_fit", label: "Target Audience Aspiration Fit" },
];

const SYSTEM_DIM_ORDER = [
  { key: "distinctiveness_at_distance", label: "Distinctiveness at distance", short: "Distance test" },
  { key: "category_navigability", label: "Category navigability", short: "Category cues" },
  { key: "family_vs_stranger", label: "Family-block test vs stranger test", short: "Family vs stranger" },
  { key: "ownability_of_fixed_assets", label: "Ownability of fixed assets", short: "Ownable assets" },
  { key: "hierarchy_of_read", label: "Hierarchy of read", short: "Hierarchy of read" },
  { key: "codified_vs_ad_hoc", label: "Codified vs ad hoc variation", short: "Codified variance" },
  { key: "scalability_future_proofing", label: "Scalability & future-proofing (incl. cultural fit)", short: "Scalability" },
];

/**
 * Pick the correct dimension order for an assessment. System-level audits
 * emit a different set of 7 keys (architecture rubric) than pack audits, so
 * the UI has to know which list to iterate.
 *
 * If the assessment already has some `dimensions` stored (background job
 * partially or fully completed) and none of the known keys match, we fall
 * back to whatever the LLM returned — so a future rubric change won't leave
 * every dimension rendering as "pending".
 */
export function getDimOrder(assessment) {
  const kind = assessment?.assessment_kind || "primary";
  const preset = kind === "system_level" ? SYSTEM_DIM_ORDER : PACK_DIM_ORDER;
  const dims = assessment?.dimensions || [];
  if (!dims.length) return preset;
  const presetKeys = new Set(preset.map((d) => d.key));
  const anyKnown = dims.some((d) => presetKeys.has(d.key));
  if (anyKnown) return preset;
  // Unknown / custom key set — derive an order from what the LLM actually
  // returned. Preserves labels the backend supplied.
  return dims.map((d) => ({ key: d.key, label: d.label || d.key }));
}

// Legacy default export kept for callers that only care about the pack rubric
// (e.g. `AssessmentDiff` still assumes primary audits). New callers should
// use `getDimOrder(assessment)` instead.
const DIM_ORDER = PACK_DIM_ORDER;

export { DIM_ORDER, PACK_DIM_ORDER, SYSTEM_DIM_ORDER };

export default function DimensionCards({ dimByKey, cmpByKey, dimOrder }) {
  const order = dimOrder || PACK_DIM_ORDER;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {order.map((od, i) => {
        const d = dimByKey[od.key];
        if (!d) return <PendingCard key={od.key} idx={i} label={od.label} testKey={od.key} />;
        return <DimensionCard key={od.key} idx={i} d={d} fallbackLabel={od.label} cmp={cmpByKey[od.key]} />;
      })}
    </div>
  );
}

function PendingCard({ idx, label, testKey }) {
  return (
    <div className="v-surface" style={{ padding: 24, opacity: 0.55 }} data-testid={`dimension-pending-${testKey}`}>
      <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11 }}>{String(idx + 1).padStart(2, "0")} · awaiting</div>
      <h3 style={{ marginTop: 4, fontSize: 22 }}>{label}</h3>
      <Skeleton w={"75%"} h={14} top={14} />
      <Skeleton w={"55%"} h={10} top={8} />
      <Skeleton w={"65%"} h={10} top={6} />
    </div>
  );
}

function DimensionCard({ idx, d, fallbackLabel, cmp }) {
  return (
    <div className="v-surface v-fade-in" style={{ padding: 24 }} data-testid={`dimension-card-${d.key}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
        <div>
          <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11 }}>{String(idx + 1).padStart(2, "0")}</div>
          <h3 style={{ marginTop: 4, fontSize: 22 }}>{d.label || fallbackLabel}</h3>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="v-score" style={{ fontSize: 48 }}>{Number(d.score || 0).toFixed(1)}</div>
          {cmp && <CompareDelta cur={d.score} prev={cmp.score} />}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ marginTop: 18 }}>
        <Block title="Strengths" items={d.strengths} accent />
        <Block title="Weaknesses" items={d.weaknesses} />
        <Block title="Improvements" items={d.improvements} />
      </div>
    </div>
  );
}

function CompareDelta({ cur, prev }) {
  const diff = Number(cur || 0) - Number(prev || 0);
  const better = diff >= 0;
  const sign = better ? "+" : "";
  return (
    <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)", marginTop: 4 }}>
      vs prev{" "}
      <b style={{ color: better ? "var(--v-accent)" : "var(--v-text-2)" }}>{sign}{diff.toFixed(1)}</b>
      {" "}(prev {Number(prev || 0).toFixed(1)})
    </div>
  );
}

function Block({ title, items, accent }) {
  return (
    <div>
      <div className="overline" style={{ color: accent ? "var(--v-accent)" : "var(--v-text-2)" }}>{title}</div>
      <ul style={{ marginTop: 8, paddingLeft: 16, lineHeight: 1.55, fontSize: 14 }}>
        {(items || []).map((it, i) => <li key={`${i}-${typeof it === "string" ? it.slice(0, 32) : i}`}>{it}</li>)}
      </ul>
    </div>
  );
}

function Skeleton({ w, h, top }) {
  return (
    <div style={{
      width: w, height: h, marginTop: top,
      background: "linear-gradient(90deg, var(--v-muted) 0%, var(--v-border) 50%, var(--v-muted) 100%)",
      backgroundSize: "200% 100%",
      animation: "v-shimmer 1.8s linear infinite",
    }} />
  );
}
